// User.types.ts - Updated based on your mongoose schema

export interface IUser {
  _id: string;
  profileImage?: string;
  fcm?: string;
  name: string;
  mobileNo: string; // This is phoneNumber in your schema
  dob: string;
  email: string;
  address?: string;
  city?: string;
  pincode?: string;
  languageName?: string;
  courseName?: string;
  standard?: string;
  role: UserRole;
  likedTestServices?: string[];
  likedBooks?: string[];
  purchasedBooks?: string[];
  purchasedTests?: string[];
  otpDetails?: {
    otp?: string;
    otpCreatedAt?: string;
  };
  password?: string;
  bookMarkedArticle?: string[];
  isDemo: boolean;
  isDisabled: boolean; // This is your status field
  createdAt: string;
  updatedAt: string;
}

export interface ICreateUser {
  name: string;
  mobileNo: string; // phoneNumber maps to mobileNo
  email: string;
  password: string;
  // Role will be assigned from backend, so not including here
  // Other fields will have defaults or be handled by backend
}

export interface IUpdateUser {
  name?: string;
  mobileNo?: string;
  email?: string;
  address?: string;
  city?: string;
  pincode?: string;
  languageName?: string;
  courseName?: string;
  standard?: string;
  isDisabled?: boolean; // For enabling/disabling users
  dob?: string;
  password?: string; // For password updates
}

export interface IUserFormData {
  name: string;
  phoneNumber: string; // Will be mapped to mobileNo
  email: string;
  password: string;
  confirmPassword: string;
}

export interface IEditUserFormData {
  name: string;
  phoneNumber: string; // Will be mapped to mobileNo
  email: string;
  address?: string;
  city?: string;
  pincode?: string;
  dob?: string;
  isDisabled?: boolean;
  // Password fields are separate for edit form
}

export type UserRole = "STUDENT" | "ADMIN";

export interface IUserFilters {
  role?: UserRole;
  isDisabled?: boolean;
  fromDate?: string;
  toDate?: string;
  search?: string;
  standard?: string;
  courseName?: string;
  languageName?: string;
}

export interface IUserListResponse {
  users: IUser[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface IUserApiResponse {
  success: boolean;
  message: string;
  data?: IUser | IUser[];
  error?: string;
}

// Props interfaces for components
export interface IMasterUsersProps {
  className?: string;
}

export interface IAddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (user: IUser) => void;
}

export interface IEditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onSuccess?: (user: IUser) => void;
}

export interface IUserCardProps {
  user: IUser;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onStatusToggle: (id: string, status: boolean) => void;
}

// Hook return type
export interface IUseMasterUsersReturn {
  selectedUserId: string | null;
  usersData: IUser[];
  loading: boolean;
  error: string | null;
  filters: IUserFilters;
  handleEdit: (id: string) => void;
  handleDelete: (id: string) => Promise<void>;
  handleStatusUpdate: (id: string, isDisabled: boolean) => Promise<void>;
  handleDateFilter: (fromDate: Date, toDate: Date) => Promise<void>;
  handleAdd: () => void;
  addUser: (userData: ICreateUser) => Promise<IUser>;
  updateUser: (id: string, userData: IUpdateUser) => Promise<IUser>;
  getUserById: (id: string) => IUser | undefined;
  clearSelectedUser: () => void;
  fetchUsers: (filters?: IUserFilters) => Promise<void>;
  searchUsers: (query: string) => Promise<void>;
  filterByRole: (role: UserRole | null) => Promise<void>;
  filterByStatus: (isDisabled: boolean | null) => Promise<void>;
  resetFilters: () => Promise<void>;
}

// Validation rules
export const userValidationRules = {
  name: {
    required: "Name is required",
    minLength: { value: 2, message: "Name must be at least 2 characters" },
    maxLength: { value: 50, message: "Name cannot exceed 50 characters" }
  },
  phoneNumber: {
    required: "Phone number is required",
    pattern: {
      value: /^[0-9]{10}$/,
      message: "Phone number must be exactly 10 digits"
    }
  },
  email: {
    required: "Email is required",
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
      message: "Invalid email format"
    }
  },
  password: {
    required: "Password is required",
    minLength: { value: 8, message: "Password must be at least 8 characters" },
    pattern: {
      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      message: "Password must contain uppercase, lowercase, number and special character"
    }
  }
};